import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import { CalltimeLogDialog } from './CalltimeLogDialog'; // Adjust the import path as needed
import { useCookies } from "react-cookie";

interface ElapsedTimeWidgetProps {
  elapsedMinutes: number;
  durationInMinutes?: number; // Defaults to 20 minutes
  // Additional props required to fetch call logs.
  patient_id: string;
  organization_id: string;
  refreshKey?: string | number; // Key to force refresh of call logs
}

const ElapsedTimeWidget: React.FC<ElapsedTimeWidgetProps> = ({
  elapsedMinutes,
  durationInMinutes = 20,
  patient_id,
  organization_id,
  refreshKey,
}) => {
  // Validate the duration and elapsedMinutes values.
  const duration = Number(durationInMinutes) > 0 ? Number(durationInMinutes) : 20;
  const validElapsed = isNaN(Number(elapsedMinutes)) ? 0 : Number(elapsedMinutes);
  const [cookies] = useCookies(["access_token"]);
  const token = cookies.access_token;

  // Calculate progress (capped at 100%)
  const progress = Math.min(validElapsed / duration, 1);

  // Change gauge color to green when elapsed time meets/exceeds duration.
  const strokeColor = validElapsed >= duration ? 'green' : 'blue';

  // SVG gauge parameters.
  const radius = 30;
  const stroke = 4;
  const normalizedRadius = radius - stroke * 2;
  const circumference = 2 * Math.PI * normalizedRadius;
  const strokeDashoffset = circumference - progress * circumference;

  // Convert validElapsed (in minutes) to total seconds.
  const totalSeconds = Math.floor(validElapsed * 60);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  // Format as "mm:ss" with zero padding.
  const formattedTime = `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;

  // Local state to control dialog open.
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <div
        style={{
          position: 'relative',
          width: radius * 2,
          height: radius * 2,
          cursor: 'pointer',
        }}
        onClick={() => setDialogOpen(true)}
      >
        <svg height={radius * 2} width={radius * 2}>
          {/* Background circle */}
          <circle
            stroke="#e6e6e6"
            fill="transparent"
            strokeWidth={stroke}
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
          {/* Progress circle */}
          <circle
            stroke={strokeColor}
            fill="transparent"
            strokeWidth={stroke}
            strokeDasharray={`${circumference} ${circumference}`}
            style={{ strokeDashoffset, transition: 'stroke-dashoffset 1s linear' }}
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
        </svg>
        <Typography
          variant="caption"
          align="center"
          display="block"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          {formattedTime}
        </Typography>
      </div>
      {/* Dialog moved outside the clickable container */}
      <CalltimeLogDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        patient_id={patient_id}
        organization_id={organization_id}
        token={token}
        refreshKey={refreshKey}
      />
    </>
  );
};

export default ElapsedTimeWidget;
